import { useEffect, useState } from "react";
import Eyebrow from "@/components/base/Eyebrow";
import Tag from "@/components/base/Tag";

type TagTone = "accent" | "primary" | "secondary";

interface SessionRow {
  key: string;
  name: string;
  /** GMT start/end in whole hours. End may wrap past midnight. */
  start: number;
  end: number;
  hours: string;
  liquidity: string;
  tone: TagTone;
  spread: string;
  /** Lower bound of the spread range, used to pick the deepest live session. */
  spreadLow: number;
  /** The overlap window is the peak of the day, not a separate exchange. */
  peak?: boolean;
}

const SESSIONS: SessionRow[] = [
  {
    key: "sydney",
    name: "Sydney",
    start: 22,
    end: 7,
    hours: "22:00 – 07:00",
    liquidity: "Low",
    tone: "accent",
    spread: "1.8 – 2.5 pips",
    spreadLow: 1.8,
  },
  {
    key: "tokyo",
    name: "Tokyo",
    start: 0,
    end: 9,
    hours: "00:00 – 09:00",
    liquidity: "Medium",
    tone: "secondary",
    spread: "1.2 – 1.6 pips",
    spreadLow: 1.2,
  },
  {
    key: "london",
    name: "London",
    start: 8,
    end: 17,
    hours: "08:00 – 17:00",
    liquidity: "Very high",
    tone: "primary",
    spread: "0.6 – 1.2 pips",
    spreadLow: 0.6,
  },
  {
    key: "overlap",
    name: "London–NY overlap",
    start: 13,
    end: 17,
    hours: "13:00 – 17:00",
    liquidity: "Peak",
    tone: "primary",
    spread: "0.3 – 0.6 pips",
    spreadLow: 0.3,
    peak: true,
  },
  {
    key: "new-york",
    name: "New York",
    start: 13,
    end: 22,
    hours: "13:00 – 22:00",
    liquidity: "Very high",
    tone: "primary",
    spread: "0.6 – 1.2 pips",
    spreadLow: 0.6,
  },
];

const minutesOfDay = (date: Date): number => date.getUTCHours() * 60 + date.getUTCMinutes();

/** Handles the Sydney window, which runs across midnight GMT. */
const isWithin = (minutes: number, start: number, end: number): boolean => {
  const open = start * 60;
  const close = end * 60;
  if (open === close) return true;
  if (open < close) return minutes >= open && minutes < close;
  return minutes >= open || minutes < close;
};

const clockLabel = (date: Date): string =>
  `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;

const listNames = (names: string[]): string => {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

export default function SessionLiquidity() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const nowMinutes = minutesOfDay(now);
  const activeRows = SESSIONS.filter((row) => isWithin(nowMinutes, row.start, row.end));
  const activeKeys = activeRows.map((row) => row.key);
  const peakRow = activeRows.find((row) => row.peak);
  const openRows = activeRows.filter((row) => !row.peak);
  const deepest = [...openRows].sort((a, b) => a.spreadLow - b.spreadLow)[0];
  const openNames = openRows.map((row) => row.name);

  const status = peakRow
    ? `London and New York are both open — about ${peakRow.spread} on EUR/USD, the tightest of the day.`
    : deepest
      ? openRows.length > 1
        ? `${listNames(openNames)} ${openRows.length === 2 ? "are both" : "are all"} open — the tighter book is ${deepest.name} at about ${deepest.spread} on EUR/USD.`
        : `${deepest.name} is open — about ${deepest.spread} on EUR/USD.`
      : "The quiet hour between sessions — spreads are usually at their widest here.";

  return (
    <section className="px-4 md:px-8 py-16 md:py-24 bg-foreground-950">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <Eyebrow tone="onDark">Timing</Eyebrow>
          <h2 className="mt-4 font-heading font-black uppercase leading-[0.92] tracking-tight text-background-50 text-3xl md:text-5xl">
            Liquidity changes through the day
          </h2>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-background-300">
            The deeper the book, the tighter the spread and the less slippage you suffer. On
            EUR/USD you pay roughly 2 pips in a quiet Sydney hour and roughly 0.4 pips in the
            London–New York overlap. At $10 a pip on a standard lot that is $4 to $20 per lot, on
            every single fill.
          </p>
        </div>

        <div className="mt-12 overflow-x-auto border-2 border-primary-500">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <caption className="sr-only">
              Forex trading sessions by GMT hours, liquidity and the typical EUR/USD spread in each
              one.
            </caption>
            <thead>
              <tr className="bg-primary-500 text-foreground-950">
                <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">
                  Session
                </th>
                <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">
                  Hours (GMT)
                </th>
                <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em] border-r-2 border-foreground-950">
                  Liquidity
                </th>
                <th scope="col" className="px-5 py-4 font-label text-[11px] uppercase tracking-[0.16em]">
                  Typical EUR/USD spread
                </th>
              </tr>
            </thead>
            <tbody>
              {SESSIONS.map((session) => {
                const live = activeKeys.includes(session.key);
                return (
                  <tr
                    key={session.key}
                    className={`border-t-2 border-primary-500 ${
                      live ? "bg-primary-500/15" : session.peak ? "bg-primary-500/5" : ""
                    }`}
                  >
                    <td className="px-5 py-5 border-r-2 border-primary-500/30">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading font-bold uppercase tracking-tight text-background-50">
                          {session.name}
                        </span>
                        {live && (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="block w-1.5 h-1.5 bg-accent-400 animate-pulse"
                              aria-hidden="true"
                            ></span>
                            <span className="font-label text-[10px] uppercase tracking-[0.16em] font-semibold text-accent-400">
                              Live
                            </span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-5 font-label text-xs text-background-300 border-r-2 border-primary-500/30 whitespace-nowrap">
                      {session.hours}
                    </td>
                    <td className="px-5 py-5 border-r-2 border-primary-500/30">
                      <Tag tone={session.tone}>{session.liquidity}</Tag>
                    </td>
                    <td className="px-5 py-5 font-label text-sm tabular-nums text-background-50 whitespace-nowrap">
                      {session.spread}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-8">
          <div className="flex items-start gap-3">
            <span className="mt-[3px] shrink-0 font-label text-[11px] uppercase tracking-[0.16em] font-semibold text-accent-400 whitespace-nowrap">
              Now {clockLabel(now)} GMT
            </span>
            <p className="text-sm leading-relaxed text-background-300">{status}</p>
          </div>
          <p className="max-w-md text-xs leading-relaxed text-background-500 md:text-right">
            Hours shift one hour earlier when London, New York or Sydney moves to daylight saving.
            Spreads are indicative, not a live quote.
          </p>
        </div>
      </div>
    </section>
  );
}